import { useState } from 'react'
import { Loader2, Plus, Check } from 'lucide-react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog'

type AddedChannel = {
  id: string
  youtube_channel_id: string
  title: string
  thumbnail_url: string
}

/**
 * Add (and follow) any YouTube channel by URL or @handle — including channels
 * the user isn't subscribed to. This is the "track your competitors" entry
 * point. `onAdded` lets the parent refresh its channel list/selection.
 */
export function AddChannelDialog({
  onAdded,
  trigger,
}: {
  onAdded?: (channel: AddedChannel) => void
  trigger?: React.ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastAdded, setLastAdded] = useState<string | null>(null)

  const submit = async () => {
    const value = input.trim()
    if (!value || loading) return
    setLoading(true)
    setError(null)
    try {
      const { channel, alreadyFollowed } = await api.addChannelByUrl(value)
      setLastAdded(alreadyFollowed ? `Already tracking ${channel.title}.` : `Now tracking ${channel.title}.`)
      setInput('')
      onAdded?.(channel)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't add that channel.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) {
          setInput('')
          setError(null)
          setLastAdded(null)
        }
      }}
    >
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="outline" size="sm" className="gap-1.5">
            <Plus className="h-3.5 w-3.5" />
            Add channel
          </Button>
        )}
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Track any channel</DialogTitle>
          <DialogDescription>
            Drop in a YouTube URL or @handle — even a competitor you don&rsquo;t subscribe to. Rundown
            starts covering it in your feed and niche brief.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Input
            autoFocus
            placeholder="youtube.com/@mkbhd  or  @mkbhd"
            value={input}
            onChange={(e) => {
              setInput(e.target.value)
              setError(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                submit()
              }
            }}
          />

          {error && <p className="text-sm text-destructive">{error}</p>}
          {lastAdded && !error && (
            <p className="flex items-center gap-1.5 text-sm text-emerald-600 dark:text-emerald-400">
              <Check className="h-3.5 w-3.5" />
              {lastAdded}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Done
            </Button>
            <Button size="sm" onClick={submit} disabled={loading || !input.trim()} className="gap-1.5">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              {loading ? 'Adding…' : 'Add & track'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
